/**
 * @file Collects the unique entity name tags of a project's properties for export.
 */
import { FUND } from "@api/services/backstage/shapes/ProjectTypeEnum";

import type { Project, Property, PropertyTag } from "../types";

const ENTITY_NAME_KEY = "Entity Name";
const ENTITY_NAMES_SEPARATOR = ", ";

type GetProperty = (propertyId: string) => Property;

/**
 * Builds the comma-separated list of unique entity names tagged on a project's properties.
 */
const getEntityNames = (properties: Property[], getProperty: GetProperty, project: Project): string => {
  const { investmentSubjectIds = [], id: projectId, type } = project;
  const isFund = type === FUND;

  const targetProperties = isFund
    ? investmentSubjectIds.map((propertyId) => getProperty(propertyId))
    : properties.filter((property) => property.projectId === projectId);

  const propertiesTags = targetProperties
    .map(({ tags }) => tags)
    .reduce((allTags: PropertyTag[], tags) => allTags.concat(tags), []);

  const entityNames = propertiesTags
    .filter(({ label }) => label === ENTITY_NAME_KEY)
    .filter(({ value }) => !!value && value.length > 0)
    .map(({ value }) => value);

  const uniqueEntityNames = Array.from(new Set(entityNames));

  return uniqueEntityNames.join(ENTITY_NAMES_SEPARATOR);
};

export default getEntityNames;
